<template>
  <v-card class="modal-carta">
    <div class="cabecera">
      <h2 class="titulo">
        Cargar carta
      </h2>
      <v-btn
        icon
        variant="text"
        size="small"
        :disabled="enCurso"
        @click="cerrar"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- Sin hospedaje vigente no se deja subir: el servidor lo rechazaria
         igual (402), pero mejor decirlo antes de que elija el archivo -->
    <template v-if="conHospedaje === false && paso === 'elegir'">
      <v-alert
        type="info"
        variant="tonal"
        class="mb-3"
      >
        La carga de carta está disponible para empresas con hospedaje
        vigente. Activa o renueva tu hospedaje y vuelve a intentarlo.
      </v-alert>

      <v-btn
        class="bg-blue"
        block
        @click="irAPagos"
      >
        Ir a mis pagos
      </v-btn>
    </template>

    <!-- 1. Elegir archivo -->
    <template v-else-if="paso === 'elegir'">
      <p class="ayuda">
        Sube una foto o un PDF de tu carta o menú. La leemos y creamos tus
        productos con su nombre, precio, categoría y descripción.
      </p>

      <v-file-input
        v-model="archivo"
        label="Carta (JPG, PNG, WEBP o PDF, máx. 10 MB)"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        variant="outlined"
        prepend-icon=""
        prepend-inner-icon="mdi-file-document-outline"
        :error-messages="errorArchivo"
        show-size
      />

      <p class="nota">
        Si un nombre o un precio no se lee con claridad, ese producto no se
        crea y te avisamos. Los productos que ya tienes no se repiten.
      </p>

      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        density="comfortable"
        class="mb-3"
      >
        {{ error }}
      </v-alert>

      <v-btn
        class="bg-blue"
        block
        :loading="subiendo"
        :disabled="!archivoElegido || !!errorArchivo"
        @click="subir"
      >
        <v-icon start>
          mdi-upload
        </v-icon>
        Leer carta y crear productos
      </v-btn>
    </template>

    <!-- 2. Claude trabajando -->
    <div
      v-else-if="paso === 'leyendo'"
      class="estado"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="44"
        width="4"
      />
      <strong>Leyendo tu carta...</strong>
      <span v-if="estado && estado.creados > 0">
        {{ estado.creados }} producto(s) creado(s) hasta ahora
      </span>
      <span class="nota">
        Puede tardar unos minutos si la carta es larga. No cierres esta ventana.
      </span>
    </div>

    <!-- 3. Resultado -->
    <template v-else>
      <v-alert
        :type="estado.estado === 'fallida' ? 'error' : (estado.creados > 0 ? 'success' : 'warning')"
        variant="tonal"
        class="mb-3"
      >
        <template v-if="estado.estado === 'fallida'">
          {{ estado.mensaje || 'No se pudo procesar la carta' }}
          <span v-if="estado.creados > 0">
            Alcanzaron a crearse {{ estado.creados }} producto(s).
          </span>
        </template>
        <template v-else-if="estado.creados > 0">
          Se crearon {{ estado.creados }} producto(s).
        </template>
        <template v-else>
          No se creó ningún producto.
        </template>
      </v-alert>

      <div
        v-if="estado.productosCreados && estado.productosCreados.length"
        class="bloque"
      >
        <div class="bloque-titulo">
          Creados
        </div>
        <ul class="lista">
          <li
            v-for="(nombre, i) in estado.productosCreados"
            :key="'c' + i"
          >
            {{ nombre }}
          </li>
        </ul>
      </div>

      <div
        v-if="estado.omitidos && estado.omitidos.length"
        class="bloque"
      >
        <div class="bloque-titulo">
          No se crearon
        </div>
        <ul class="lista">
          <li
            v-for="(o, i) in estado.omitidos"
            :key="'o' + i"
          >
            <strong>{{ o.nombre || 'Sin nombre' }}</strong>: {{ o.motivo }}
          </li>
        </ul>
      </div>

      <div
        v-if="estado.alertas && estado.alertas.length"
        class="bloque"
      >
        <div class="bloque-titulo">
          Avisos
        </div>
        <ul class="lista">
          <li
            v-for="(a, i) in estado.alertas"
            :key="'a' + i"
          >
            {{ a }}
          </li>
        </ul>
      </div>

      <p
        v-if="estado.creados > 0"
        class="nota"
      >
        Revisa los precios en tu lista de productos: puedes editar o borrar
        cualquiera.
      </p>

      <v-btn
        class="bg-blue"
        block
        @click="cerrar"
      >
        Listo
      </v-btn>
    </template>
  </v-card>
</template>

<script>
import apisProducts from '@/apis/apisProducts'
import { mensajeDeError } from '@/utils/errores'

const MAX_BYTES = 10 * 1024 * 1024
const TIPOS = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']

/** Cada cuanto se pregunta como va la carga */
const INTERVALO_MS = 3000

/**
 * Sube la carta y muestra el avance mientras Claude crea los productos en
 * el servidor (ver MenuImportService en el backend).
 *
 * El servidor responde enseguida con un idCarga y el trabajo sigue alla; este
 * modal solo pregunta como va cada pocos segundos.
 */
export default {
  name: 'CargarCartaModal',
  props: {
    idCompany: {
      type: Number,
      required: true
    },
    conHospedaje: {
      type: Boolean,
      default: null
    }
  },
  emits: ['onClose', 'alTerminar'],
  data() {
    return {
      paso: 'elegir',
      archivo: null,
      subiendo: false,
      error: null,
      estado: null,
      temporizador: null
    }
  },
  computed: {
    // Segun la version de Vuetify, v-file-input entrega el archivo o una lista
    archivoElegido() {
      return Array.isArray(this.archivo) ? this.archivo[0] : this.archivo
    },
    errorArchivo() {
      const f = this.archivoElegido
      if (!f) {
        return null
      }
      if (!TIPOS.includes(f.type)) {
        return 'Debe ser una imagen (JPG, PNG, WEBP) o un PDF'
      }
      if (f.size > MAX_BYTES) {
        return 'La carta no puede pesar más de 10 MB'
      }
      return null
    },
    enCurso() {
      return this.subiendo || this.paso === 'leyendo'
    }
  },
  beforeUnmount() {
    clearTimeout(this.temporizador)
  },
  methods: {
    subir() {
      this.error = null
      this.subiendo = true

      apisProducts.cargarCarta(this.idCompany, this.archivoElegido)
        .then(estado => {
          this.estado = estado
          this.paso = 'leyendo'
          this.consultar()
        })
        .catch(error => {
          this.error = mensajeDeError(error, 'No se pudo subir la carta')
        })
        .finally(() => {
          this.subiendo = false
        })
    },
    consultar() {
      this.temporizador = setTimeout(() => {
        apisProducts.estadoCarta(this.estado.idCarga)
          .then(estado => {
            this.estado = estado

            if (estado.estado === 'terminada' || estado.estado === 'fallida') {
              this.paso = 'resultado'
              this.$emit('alTerminar', estado)
            } else {
              this.consultar()
            }
          })
          // Un fallo de red suelto no corta la espera: se vuelve a preguntar
          .catch(() => this.consultar())
      }, INTERVALO_MS)
    },
    irAPagos() {
      this.cerrar()
      this.$router.push({ name: 'MyPaymentsView' })
    },
    cerrar() {
      clearTimeout(this.temporizador)
      this.paso = 'elegir'
      this.archivo = null
      this.estado = null
      this.error = null
      this.$emit('onClose')
    }
  }
}
</script>

<style scoped>
.modal-carta {
  padding: 16px;
}
.cabecera {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.titulo {
  font-size: 1.1rem;
  font-weight: 700;
}
.ayuda {
  font-size: 0.9rem;
  color: #475467;
  margin-bottom: 14px;
}
.nota {
  font-size: 0.8rem;
  color: #667085;
  margin-bottom: 12px;
}
.estado {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 32px 8px;
  text-align: center;
}
.bloque {
  margin-bottom: 12px;
}
.bloque-titulo {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #1976d2;
  margin-bottom: 4px;
}
.lista {
  font-size: 0.88rem;
  padding-left: 18px;
  max-height: 180px;
  overflow-y: auto;
}
</style>
